import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import { problemTitle } from './usePartsLibrary'

/**
 * UID-167 (D3) — the "Create revision" service seam, calling the D2 handler
 * for `POST /parts/{partId}/revisions` (D0's frozen contract). Mirrors
 * `edit-part-config-service.ts`'s discriminated-outcome shape 1:1 (same
 * `field_errors` / `forbidden` / `not_found` / `error` split, same reason:
 * the modal renders every failure as TEXT, WCAG 3.3.1, never branching on a
 * raw HTTP status itself).
 */
export type CreatePartAuthoredRevisionRequest =
  components['schemas']['CreatePartAuthoredRevisionRequest']
export type PartAuthoredRevision = components['schemas']['PartAuthoredRevision']

export interface PartRevisionFieldError {
  readonly field: string
  readonly detail: string
}

/** The 400 Problem+JSON body carries an `errors` extension member the
 * generated `Problem` schema doesn't type (D2's structured field errors,
 * same posture as C2's) — read defensively at this one call site rather
 * than widening the generated `Problem` type itself. */
interface RevisionInvalidProblem {
  readonly errors?: readonly PartRevisionFieldError[]
}

export type CreatePartRevisionOutcome =
  | { readonly ok: true; readonly revision: PartAuthoredRevision }
  | {
      readonly ok: false
      readonly kind: 'field_errors'
      readonly errors: readonly PartRevisionFieldError[]
    }
  | {
      readonly ok: false
      readonly kind: 'forbidden' | 'not_found' | 'error'
      readonly message: string
    }

export interface PartRevisionService {
  createRevision(
    partId: string,
    request: CreatePartAuthoredRevisionRequest,
  ): Promise<CreatePartRevisionOutcome>
}

/**
 * REAL BFF-backed submit for `CreateRevisionModal` (UID-167/D3) — calls the
 * D2 `POST /parts/{partId}/revisions` handler and maps its outcomes (201
 * minted `PartAuthoredRevision` / 400 structured field errors / 403
 * capability-denied / 404 tenant-scoped-or-unknown-attachment-no-leak) onto
 * a discriminated result, same posture as `bffEditPartConfig`.
 */
async function bffCreatePartRevision(
  partId: string,
  request: CreatePartAuthoredRevisionRequest,
): Promise<CreatePartRevisionOutcome> {
  const { data, error, response } = await apiClient.POST('/parts/{partId}/revisions', {
    params: { path: { partId } },
    body: request,
  })

  if (response.status === 400) {
    const body = error as RevisionInvalidProblem | undefined
    return { ok: false, kind: 'field_errors', errors: body?.errors ?? [] }
  }
  if (response.status === 403) {
    return {
      ok: false,
      kind: 'forbidden',
      message: "You don't have permission to author a revision of this part.",
    }
  }
  if (response.status === 404) {
    return {
      ok: false,
      kind: 'not_found',
      message:
        'This part — or one of the uploaded files — could not be found. Try uploading again.',
    }
  }
  if (error || !data) {
    return {
      ok: false,
      kind: 'error',
      message: problemTitle(error, "We couldn't create this revision. Try again."),
    }
  }
  return { ok: true, revision: data }
}

export const defaultPartRevisionService: PartRevisionService = {
  createRevision: bffCreatePartRevision,
}
